import { useMemo } from 'react';
import styled from 'styled-components';
import { useErrorHandlerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { capitalizeFirstLetter, incidentTypeMapper } from '@/helpers';
import { GetIncidentsForListQuery, IncidentFilterInput, useGetIncidentsForListQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import { IncidentStatusOptionPill, LoadingSpinner, PriorityPill, DebtBadge } from '../atoms';
import textsData from '@/textData/ListTablesTexts.json';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { Col, Row } from 'react-grid-system';

type IncidentsPaginated =
  Extract<GetIncidentsForListQuery['incidents'], { __typename?: 'IncidentsPaginatedOutput' }>;

type IncidentsForList = NonNullable<IncidentsPaginated>['items'][number];

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 2fr 1fr 1fr 1fr 1fr 64px;
`

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: solid 1px lightgray;
    background-color: ${({ theme }) => theme.colors.light};
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`

const readableDate = (date: Date) => {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

type IncidentsListTableProps = {
  filters: IncidentFilterInput;
};

export const IncidentsListTable = ({ filters }: IncidentsListTableProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const navigate = useNavigate();
  const { open: errorHandle } = useErrorHandlerContext();
  const { setListState } = useListNavigationStateContext();

  const { data, error, loading } = useGetIncidentsForListQuery({
    variables: {
      input: filters,
    },
    fetchPolicy: 'network-only',
  });

  const incidents = data?.incidents;
  const isPaginated = incidents && incidents.__typename === 'IncidentsPaginatedOutput';
  const actionMessage = incidents && incidents.__typename === 'ActionResponseMessageOutput'
    ? incidents
    : null;

  const dataFlatter = (data: IncidentsForList[] | undefined) => {
    if (!data) return [];

    return data.map((element) => ({
      id: element.id,
      propertyName: capitalizeFirstLetter(element.property?.name ?? ''),
      unity: element.unity ? `${element.unity?.name}` : texts.noData.unity,
      tenant: element.tenant
        ? {
            firstName: element.tenant.firstName,
            lastName: element.tenant.lastName,
            hasDebt: element.tenant.hasDebt ?? false,
          }
        : null,
      supplier: capitalizeFirstLetter(
        element.supplier ? `${element.supplier.firstName} ${element.supplier.lastName}` : 'not assigned'
      ),
      type: incidentTypeMapper(element.incidentTypeOption, userLanguage),
      priority: element.priorityOption,
      startDate: readableDate(element.startDate),
      subject: element.subject,
      status: element.statusOption,
    }));
  };

  const flattenedData = useMemo(
    () => (isPaginated ? dataFlatter((incidents as IncidentsPaginated).items) : null),
    [incidents, isPaginated, texts, userLanguage]
  );

  if (error) errorHandle(error);

  if (loading) {
    return (
      <Row justify="center">
        <Col xs="content">
          <LoadingSpinner text />
        </Col>
      </Row>
    );
  }

  if (actionMessage) {
    return (
      <Row>
        <Col>
          <span>
            <b>{texts.noData.table}</b> {actionMessage.message}
          </span>
        </Col>
      </Row>
    );
  }

  if (!flattenedData) {
    return (
      <Row>
        <Col>
          <span>
            <b>{texts.noData.table}</b>
          </span>
        </Col>
      </Row>
    );
  }

  return (
    <StyledGridTable>
      <StyledGridHeader>
        <div>{texts.incidentsTable.property}</div>
        <div>{texts.incidentsTable.unity}</div>
        <div>{userLanguage === 'es' ? 'Inquilino' : 'Tenant'}</div>
        <div>{texts.incidentsTable.subject}</div>
        <div>{texts.incidentsTable.supplier}</div>
        <div>{texts.incidentsTable.startDate}</div>
        <div>{texts.incidentsTable.priority}</div>
        <div>{texts.incidentsTable.status}</div>
        <div></div>
      </StyledGridHeader>
      {flattenedData.map((incident) => (
        <StyledGridRow key={incident.id}>
          <div>{incident.propertyName}</div>
          <div>{incident.unity}</div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            {incident.tenant 
              ? (
                <>
                  <span>{capitalizeFirstLetter(incident.tenant.firstName)} {capitalizeFirstLetter(incident.tenant.lastName)}</span>
                  <DebtBadge hasDebt={incident.tenant.hasDebt} />
                </>
              )
              : '-'
            }
          </div>
          <div>{incident.subject}</div>
          <div>{incident.supplier}</div>
          <div>{incident.startDate}</div>
          <div><PriorityPill option={incident.priority} /></div>
          <div><IncidentStatusOptionPill option={incident.status} /></div>
          <div>
            <ButtonWithDropDownMenu
              options={[
                {
                  item: 'Detalles',
                  action: () => {
                    setListState('incidents', {
                      currentPage: parseInt(filters.page || '1'),
                      filters,
                      selectedItemId: incident.id,
                    });
                    navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, { state: { id: incident.id } });
                  },
                },
              ]}
              position="left"
            />
          </div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  );
};
